import React, { useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, Check, X } from 'lucide-react';
import type { SectionScore } from '../api/seoApi';
import { useSiteHealth } from '../hooks/useAudit';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { PageHeader } from '@/shared/ui';
import styles from './SEODashboardPage.module.css';

const TECHNICAL_CHECK_IDS = ['T01', 'T02', 'T03', 'T04', 'T05', 'T12', 'T13', 'T14', 'T16', 'T17'];
const SCHEMA_CHECK_IDS = [
  'S01',
  'S02',
  'S03',
  'S04',
  'S05',
  'S06',
  'S07',
  'S08',
  'S09',
  'S10',
  'S11',
];

type CwvStatus = 'fast' | 'average' | 'slow';

function cwvStatus(name: string, value: number | undefined): CwvStatus {
  if (value == null) return 'slow';
  if (name === 'LCP') return value < 2.5 ? 'fast' : value < 4 ? 'average' : 'slow';
  if (name === 'FCP') return value < 1.8 ? 'fast' : value < 3 ? 'average' : 'slow';
  if (name === 'INP') return value < 200 ? 'fast' : value < 500 ? 'average' : 'slow';
  if (name === 'CLS') return value < 0.1 ? 'fast' : value < 0.25 ? 'average' : 'slow';
  if (name === 'TTFB') return value < 800 ? 'fast' : value < 1800 ? 'average' : 'slow';
  return 'slow';
}

function statusClass(s: CwvStatus) {
  return s === 'fast' ? styles.checkPass : s === 'average' ? styles.positionPoor : styles.checkFail;
}

function statusLabel(s: CwvStatus) {
  return s === 'fast' ? 'Good' : s === 'average' ? 'Needs Work' : 'Critical';
}

const CWV_LABELS: Record<string, string> = {
  LCP: 'Page Load (LCP)',
  FCP: 'First Paint (FCP)',
  INP: 'Interactivity (INP)',
  CLS: 'Visual Stability (CLS)',
  TTFB: 'Server Response (TTFB)',
};

export function SiteHealthPage() {
  const { data, isLoading } = useSiteHealth();
  const [cwvDevice, setCwvDevice] = useState<'mobile' | 'desktop'>('mobile');

  const audit = data?.audit;
  const analysis = data?.analysis;

  const allChecks = Object.values((audit?.sectionScores ?? {}) as Record<string, SectionScore>)
    .flatMap((section) => section?.checks ?? [])
    .map((c) => ({ ...c, note: c.note ?? undefined }));

  const technicalChecks = allChecks.filter((c) => TECHNICAL_CHECK_IDS.includes(c.id));
  const schemaChecks = allChecks.filter((c) => SCHEMA_CHECK_IDS.includes(c.id));

  // Parse pagespeed for CWV
  const pagespeedResults = analysis?.pagespeedResults ?? [];

  const cwvData = pagespeedResults.filter((p) => p.strategy === cwvDevice)[0];
  const m = cwvData?.metrics ?? {};

  const cwvMetrics = [
    {
      name: 'LCP',
      value: m.lcp != null ? `${m.lcp.toFixed(1)}s` : '—',
      rawValue: m.lcp,
      target: '< 2.5s',
    },
    {
      name: 'FCP',
      value: m.fcp != null ? `${m.fcp.toFixed(1)}s` : '—',
      rawValue: m.fcp,
      target: '< 1.8s',
    },
    {
      name: 'INP',
      value: m.inp != null ? `${Math.round(m.inp)}ms` : '—',
      rawValue: m.inp,
      target: '< 200ms',
    },
    {
      name: 'CLS',
      value: m.cls != null ? m.cls.toFixed(3) : '—',
      rawValue: m.cls,
      target: '< 0.1',
    },
    {
      name: 'TTFB',
      value: m.ttfb != null ? `${Math.round(m.ttfb)}ms` : '—',
      rawValue: m.ttfb,
      target: '< 800ms',
    },
  ];

  const llmsCheck = allChecks.find((c) => c.id === 'AEO08');
  const llmsPresent = llmsCheck?.status === 'pass';
  const robotsCheck = allChecks.find((c) => c.id === 'T01');
  const robotsPresent = robotsCheck?.status === 'pass';

  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Website Health" />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Website Health"
        subtitle="Core Web Vitals + technical and schema checks from your latest audit"
        timestamp={audit?.createdAt}
      />

      {/* Core Web Vitals */}
      <div className={styles.cwvCard}>
        <div className={styles.cwvHeader}>
          <span className={styles.cwvHeaderTitle}>Core Web Vitals</span>
          <div className={styles.filterGroup}>
            {(['mobile', 'desktop'] as const).map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => setCwvDevice(d)}
                className={clsx(styles.filterPill, cwvDevice === d && styles.filterPillActive)}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        {cwvData ? (
          <>
            <div className={styles.cwvGrid}>
              {cwvMetrics.map((metric) => {
                const s = cwvStatus(metric.name, metric.rawValue);
                return (
                  <div key={metric.name} className={styles.cwvMetric}>
                    <div className={styles.cwvLabel}>{CWV_LABELS[metric.name] ?? metric.name}</div>
                    <div className={clsx(styles.cwvValue, statusClass(s))}>{metric.value}</div>
                    <div className={clsx(styles.cwvStatus, statusClass(s))}>{statusLabel(s)}</div>
                  </div>
                );
              })}
            </div>
            {/* Critical alert */}
            {(() => {
              const lcpMetric = cwvMetrics.find((m) => m.name === 'LCP');
              const lcpSlow = lcpMetric && cwvStatus('LCP', lcpMetric.rawValue) === 'slow';
              if (lcpSlow) {
                return (
                  <div className={styles.cwvAlert}>
                    <AlertTriangle size={14} aria-hidden /> Your site takes {lcpMetric.value} to
                    load on {cwvDevice} — most patients will leave before it finishes. Google
                    recommends under 2.5 seconds.
                  </div>
                );
              }
              const slowMetrics = cwvMetrics.filter(
                (m) => cwvStatus(m.name, m.rawValue) === 'slow',
              );
              if (slowMetrics.length > 0) {
                return (
                  <div className={styles.cwvAlert}>
                    <AlertTriangle size={14} aria-hidden /> Your site has performance issues on{' '}
                    {cwvDevice} that may hurt your Google ranking and patient experience.
                  </div>
                );
              }
              return null;
            })()}
          </>
        ) : (
          <div className={styles.cwvEmpty}>No PageSpeed data available for this device type.</div>
        )}
      </div>

      {/* Technical checks */}
      <div className={styles.cwvCard}>
        <div className={styles.panelHeading}>Technical Checks</div>
        {technicalChecks.length > 0 ? (
          <div className={styles.checksGrid}>
            {technicalChecks.map((c) => (
              <div
                key={c.id}
                className={clsx(styles.checkRow, c.status === 'fail' && styles.checkRowFail)}
              >
                <span
                  className={clsx(styles.checkName, c.status === 'fail' && styles.checkNameFail)}
                >
                  {c.name}
                </span>
                <span
                  className={clsx(
                    styles.checkStatus,
                    c.status === 'pass' ? styles.checkPass : styles.checkFail,
                  )}
                >
                  {c.status === 'pass' ? (
                    <>
                      <Check size={12} aria-hidden /> Pass
                    </>
                  ) : (
                    <>
                      <X size={12} aria-hidden /> Fail
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyLine}>Run an audit to see technical checks.</div>
        )}
      </div>

      {/* Schema coverage */}
      <div className={styles.cwvCard}>
        <div className={styles.cwvHeader}>
          <span className={styles.panelHeadingSm}>Schema Coverage</span>
          {schemaChecks.length > 0 && (
            <span
              className={clsx(
                styles.schemaCount,
                schemaChecks.filter((c) => c.status === 'pass').length >= 6
                  ? styles.checkPass
                  : styles.checkFail,
              )}
            >
              {schemaChecks.filter((c) => c.status === 'pass').length} of {schemaChecks.length}{' '}
              checks passing
            </span>
          )}
        </div>
        {schemaChecks.length > 0 ? (
          <div className={styles.checksGrid}>
            {schemaChecks.map((c) => (
              <div
                key={c.id}
                className={clsx(styles.checkRow, c.status === 'fail' && styles.checkRowMissing)}
              >
                <span className={clsx(styles.checkName, c.status === 'fail' && styles.checkWarn)}>
                  {c.name}
                </span>
                <span
                  className={clsx(
                    styles.checkStatus,
                    c.status === 'pass' ? styles.checkPass : styles.checkWarn,
                  )}
                >
                  {c.status === 'pass' ? <Check size={12} aria-hidden /> : 'Missing'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyLine}>Run an audit to see schema coverage.</div>
        )}
      </div>

      {/* llms.txt + robots.txt */}
      <div className={styles.txtPairGrid}>
        <div className={styles.txtCard}>
          <div className={styles.txtCardTitle}>llms.txt</div>
          <div
            className={clsx(
              styles.txtCardStatus,
              llmsPresent ? styles.checkPass : styles.checkFail,
            )}
          >
            {llmsPresent ? (
              <>
                <Check size={12} aria-hidden /> Published
              </>
            ) : (
              <>
                <X size={12} aria-hidden /> Not Found
              </>
            )}
          </div>
          <div className={styles.txtCardHint}>Guides AI crawlers to your most important pages</div>
        </div>
        <div className={styles.txtCard}>
          <div className={styles.txtCardTitle}>robots.txt</div>
          <div
            className={clsx(
              styles.txtCardStatus,
              robotsPresent ? styles.checkPass : styles.checkFail,
            )}
          >
            {robotsPresent ? (
              <>
                <Check size={12} aria-hidden /> Found
              </>
            ) : (
              <>
                <X size={12} aria-hidden /> Not Found
              </>
            )}
          </div>
          <div className={styles.txtCardHint}>Controls which pages search engines can access</div>
        </div>
      </div>
    </div>
  );
}
